<template>
  <div :class="[$style.config, $style.wrap]">
    <div :class="$style.container">
      <header :class="$style.header">
        <BaseHeading size="h2" as="h3">{{ data?.fields?.header }}</BaseHeading>
        <div v-if="data?.fields?.ctas?.length" :class="$style.ctas">
          <CallToAction
            v-for="c in data?.fields?.ctas"
            :key="c?.sys?.id"
            :cta="c"
          />
        </div>
      </header>
      <div v-if="computedRelatedArticles.length" :class="$style.cards">
        <ArticleCard
          v-for="a in computedRelatedArticles"
          :key="a?.sys?.id"
          :data="a"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeArticleSkeleton,
} from '~/types/contentful'

const { locale } = useI18n()
const { $client } = useNuxtApp()

// Async Data
const { data: articlesFromTagsData, execute: fetchRelatedArticlesByTags } =
  await useLazyAsyncData(
    'articlesFromTagsData',
    async () => {
      const currentPath = arrayAsString(useRoute().params.slug)
      const response =
        await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
          content_type: 'article',
          include: 10,
          locale: locale?.value,
          limit: 3,
          'fields.tags.sys.id[in]': [computedTags?.value],
          'fields.path[ne]': currentPath ? currentPath : '',
          order: ['-fields.publishedDateTime'],
        })
      return response?.items || []
    },
    {
      transform(input) {
        return {
          articles: input,
          fetchedAt: useUnixTimestamp(),
        }
      },
      getCachedData: (key) => useCachedData(key),
      immediate: false,
    },
  )

const props = defineProps<{
  data: TypeContentBlockWithoutUnresolvableLinksResponse
}>()

// Computed
const computedTags = computed(() => {
  const tags =
    props?.data?.fields?.references?.filter(
      (reference) =>
        reference?.sys?.contentType?.sys?.id?.toLowerCase?.() === 'tag',
    ) || []

  return tags.map((tag) => tag?.sys?.id).join(',')
})

const computedRelatedArticles = computed(() => {
  if (articlesFromTagsData?.value?.articles?.length)
    return articlesFromTagsData?.value?.articles

  const referencedArticles =
    props?.data?.fields?.references?.filter(instanceOfArticle)
  if (referencedArticles?.length) return referencedArticles

  return []
})

onMounted(() => {
  // Prioritize articles by tag
  if (computedTags?.value?.length) {
    fetchRelatedArticlesByTags()
  }
})
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 2.5em 1.25em;
  --wrap-padding--md: 3.75em;
  --wrap-padding: var(--wrap-padding--base);

  --container-max-width: 1220px;

  --header-flex-direction--base: column;
  --header-flex-direction--sm: row;
  --header-flex-direction: var(--header-flex-direction--base);

  --cards-gap--base: 1.75em;
  --cards-gap--sm: 2.5em;

  --cards-grid-template-columns--base: 1fr;
  --cards-grid-template-columns--sm: 1fr 1fr 1fr;
  --cards-grid-template-columns: var(--cards-grid-template-columns--base);

  @include media('sm') {
    --header-flex-direction: var(--header-flex-direction--sm);
    --cards-gap: var(--cards-gap--sm);
    --cards-grid-template-columns: var(--cards-grid-template-columns--sm);
  }

  @include media('md') {
    --cards-gap: var(--cards-gap--base);
    --wrap-padding: var(--wrap-padding--md);
  }
}

.wrap {
  padding: var(--wrap-padding);
}

.container {
  max-width: var(--container-max-width);
  margin: 0 auto;
}

.header {
  @include fluid-type(26px, 42px);
  font-family: var(--roboto-condensed);

  display: flex;
  flex-direction: var(--header-flex-direction);
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1em;
}

.cards {
  display: grid;
  grid-template-columns: var(--cards-grid-template-columns);
  gap: var(--cards-gap);
}
</style>
